import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type {
  AplicarSugestoesNBR13971Response,
  AtividadeModelo,
  AtividadeModeloCreateInput,
  PlanoModelo,
  PlanoModeloCreateInput,
  SugestoesNBR13971Response,
} from '../types/pmocPlanos'
import type { PaginatedResponse } from '../types'

interface PlanoModeloListParams {
  tipo_plano?: string
  categoria?: string
  origem?: string
  ativo?: boolean
  tipo_equipamento?: string
  search?: string
  page?: number
  page_size?: number
}

export function usePlanoModeloList(params?: PlanoModeloListParams) {
  return useQuery({
    queryKey: ['planos-modelo', params],
    queryFn: async () => {
      const r = await api.get<PaginatedResponse<PlanoModelo>>('/planos-modelo/', {
        params,
      })
      return r.data
    },
    staleTime: 1000 * 30,
  })
}

export function usePlanoModeloDetalhe(id: string) {
  return useQuery({
    queryKey: ['planos-modelo', id],
    queryFn: async () => {
      const r = await api.get<PlanoModelo>(`/planos-modelo/${id}/`)
      return r.data
    },
    enabled: !!id,
  })
}

export function usePlanoModeloCreate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: PlanoModeloCreateInput) => {
      const r = await api.post<PlanoModelo>('/planos-modelo/', data)
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['planos-modelo'] }),
  })
}

export function usePlanoModeloUpdate(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<PlanoModeloCreateInput>) => {
      const r = await api.patch<PlanoModelo>(`/planos-modelo/${id}/`, data)
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['planos-modelo'] })
      qc.invalidateQueries({ queryKey: ['planos-modelo', id] })
    },
  })
}

export function usePlanoModeloDelete() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/planos-modelo/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['planos-modelo'] }),
  })
}

export function usePlanoModeloCopiar() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await api.post<PlanoModelo>(`/planos-modelo/${id}/copiar/`)
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['planos-modelo'] }),
  })
}

/* ---------- Atividade Modelo ---------- */

export function useAtividadeModeloList(planoId: string) {
  return useQuery({
    queryKey: ['atividades-modelo', planoId],
    queryFn: async () => {
      const r = await api.get<PaginatedResponse<AtividadeModelo>>(
        `/planos-modelo/${planoId}/atividades/`,
      )
      return r.data
    },
    enabled: !!planoId,
  })
}

export function useAtividadeModeloCreate(planoId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: AtividadeModeloCreateInput) => {
      const r = await api.post<AtividadeModelo>(
        `/planos-modelo/${planoId}/atividades/`,
        data,
      )
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['atividades-modelo', planoId] })
      qc.invalidateQueries({ queryKey: ['planos-modelo', planoId] })
    },
  })
}

export function useAtividadeModeloUpdate(planoId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: Partial<AtividadeModeloCreateInput>
    }) => {
      const r = await api.patch<AtividadeModelo>(`/atividades-modelo/${id}/`, data)
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['atividades-modelo', planoId] })
      qc.invalidateQueries({ queryKey: ['planos-modelo', planoId] })
    },
  })
}

export function useAtividadeModeloDelete(planoId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/atividades-modelo/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['atividades-modelo', planoId] })
      qc.invalidateQueries({ queryKey: ['planos-modelo', planoId] })
    },
  })
}

export function useSugestoesNBR13971(tipoEquipamento?: string) {
  return useQuery({
    queryKey: ['nbr13971-sugestoes', tipoEquipamento],
    queryFn: async () => {
      const r = await api.get<SugestoesNBR13971Response>(
        '/nbr13971/sugestoes/',
        { params: { tipo_equipamento: tipoEquipamento } },
      )
      return r.data
    },
    enabled: !!tipoEquipamento,
    staleTime: 1000 * 60 * 10,
  })
}

export function useAplicarSugestoesNBR13971(planoId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const r = await api.post<AplicarSugestoesNBR13971Response>(
        `/planos-modelo/${planoId}/aplicar-sugestoes-nbr13971/`,
      )
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['atividades-modelo', planoId] })
      qc.invalidateQueries({ queryKey: ['planos-modelo', planoId] })
      qc.invalidateQueries({ queryKey: ['planos-modelo'] })
    },
  })
}

/* ---------- Planos compatíveis por equipamento ---------- */

export function usePlanosCompativeisEquipamento(
  equipamentoId: string,
  incluirInativos = false,
) {
  return useQuery({
    queryKey: ['planos-compativeis', equipamentoId, incluirInativos],
    queryFn: async () => {
      const r = await api.get<PaginatedResponse<PlanoModelo>>(
        `/equipamentos/${equipamentoId}/planos-compativeis/`,
        { params: { incluir_inativos: incluirInativos ? '1' : undefined } },
      )
      return r.data
    },
    enabled: !!equipamentoId,
  })
}
